import React, { useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import debounce from "lodash/debounce";
import {
    selectClientAssetData,
    selectCurrentClient,
    selectCurrentDeviceBeingEdited,
    selectCurrentPosition,
    selectCurrentProject,
    loadEditingDeviceVisibleSpans,
    selectVisibleSpansStatus,
    selectVisibleSpans,
    determineSpanName,
    setTargetStructureForDevice,
    setCurrentDeviceBeingEdited,
} from "./store/reducers/deviceLinkingAppStoreSlice";
import { CircularProgress, Grid, Switch } from "@mui/material";
import { ClientAssetDataSpan, Device } from "../types";

type VisibleSpansDeviceConfigProps = Record<string, never>;

export default function VisibleSpansDeviceConfig(_props: VisibleSpansDeviceConfigProps): JSX.Element {
    const dispatch = useAppDispatch();

    const currentPosition = useAppSelector(selectCurrentPosition);
    const currentDeviceBeingEdited = useAppSelector(selectCurrentDeviceBeingEdited) as Device;

    const client = useAppSelector(selectCurrentClient);
    const project = useAppSelector(selectCurrentProject);
    const clientAssetDataDataset = useAppSelector(selectClientAssetData);

    const visibleSpans = useAppSelector(selectVisibleSpans);
    const visibleSpansStatus = useAppSelector(selectVisibleSpansStatus);

    const reloadVisibleSpans = useCallback(
        debounce(() => {
            dispatch(loadEditingDeviceVisibleSpans());
        }, 250),
        []
    );

    useEffect(() => {
        if (!client || !project || !clientAssetDataDataset || !currentPosition || !currentDeviceBeingEdited) {
            return;
        }
        if (visibleSpansStatus !== "loading") {
            reloadVisibleSpans();
        }
    }, [client, project, clientAssetDataDataset, currentPosition, currentDeviceBeingEdited?.approx_angle]);

    const setVisibleSpanIsTargetedBySensor = useCallback(
        (span: ClientAssetDataSpan, targetedBySensor: boolean) => {
            dispatch(
                setCurrentDeviceBeingEdited({
                    ...currentDeviceBeingEdited,
                    visible_spans: [
                        ...(currentDeviceBeingEdited?.visible_spans || []).filter(
                            (existingVisibleSection) => existingVisibleSection.section_name !== span.section_name
                        ),
                        {
                            start_structure: span.start_structure,
                            end_structure: span.end_structure,
                            section_name: span.section_name,
                            targeted_by_sensor: targetedBySensor,
                        },
                    ],
                })
            );
        },
        [dispatch, currentDeviceBeingEdited]
    );

    const spanIsSetToVisibleInDeviceBeingEdited = useCallback(
        (span: ClientAssetDataSpan) => {
            const existingSpan = currentDeviceBeingEdited?.visible_spans?.find(
                (visible_span) => visible_span.section_name === span.section_name
            );
            // If the span is not in the device's visible_spans array, default to true
            // since it's in the visibleSpans query result (meaning it's visible from the device)
            return existingSpan !== undefined ? existingSpan.targeted_by_sensor : true;
        },
        [currentDeviceBeingEdited]
    );

    const generateGridEntryDom = useCallback(
        (span: ClientAssetDataSpan) => {
            const isChecked = spanIsSetToVisibleInDeviceBeingEdited(span);
            const spanName = determineSpanName(span);
            console.log(`Span: ${spanName}, isChecked: ${isChecked}`);
            return (
                <Grid container spacing={2} key={spanName}>
                    <Grid item xs={11}>
                        {spanName}
                    </Grid>
                    <Grid item xs={1}>
                        <Switch
                            checked={isChecked}
                            onChange={(event: any) => {
                                console.log(`Switch onChange for ${spanName}: ${event.target.checked}`);
                                setVisibleSpanIsTargetedBySensor(span, event.target.checked);
                            }}
                        />
                    </Grid>
                </Grid>
            );
        },
        [spanIsSetToVisibleInDeviceBeingEdited, setVisibleSpanIsTargetedBySensor]
    );

    return (
        <div>
            <h1>Visible Spans</h1>
            <p style={{ fontSize: "0.9em", color: "#666", marginTop: "-10px" }}>
                <i>Click a structure on the map to point the device towards it</i>
            </p>

            {visibleSpansStatus === "loading" ? (
                <CircularProgress />
            ) : (
                <div style={{ paddingRight: "15px" }}>
                    {visibleSpans ? (
                        visibleSpans?.map((span) => generateGridEntryDom(span))
                    ) : (
                        <p style={{ textAlign: "center" }}>
                            <i>No visible spans.</i>
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
